'use client';

import { clsx } from 'clsx';
import { FormikProps, useField } from 'formik';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { AUSTRALIAN_STATES } from '@/constants/constants';
import { postcodeRegex } from '@/constants/regex.constants';
import type { PlacePrediction } from '@/services/placesApi/placesApi';
import { isNil } from '@/utils/isNil';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

import { InputFormik } from '../Input/InputFormik';
import { InputSelectFormik } from '../Input/InputSelectFormik';
import { AddressAutocomplete } from '../AddressAutocomplete/AddressAutocomplete';
import { extractAddressFromPlaceDetails, validateStreetLength } from './FormikInputAddressAutocomplete.helpers';
import { useAddressAutocomplete } from './useAddressAutocomplete/useAddressAutocomplete';

const inputFieldClassName = 'w-full box-border [&_.inputWrapper]:min-w-0';

type InputAddressAutocompleteValidationConfig = {
  streetRequiredValidation?: string;
  streetTooLongValidation?: string;
  suburbRequiredValidation?: string;
  postcodeRequiredValidation?: string;
  postcodeFormatValidation?: string;
  stateValidationValidation?: string;
};

const maxCharacterValidation = (limit: number) => `Must be at most ${limit} characters.`;

export const getInputAddressAutocompleteValidation = ({
  streetRequiredValidation = 'Please add your delivery address',
  streetTooLongValidation = 'Please ensure your delivery address is properly formatted',
  suburbRequiredValidation = 'Please add your delivery suburb',
  postcodeRequiredValidation = 'Enter your postcode',
  postcodeFormatValidation = 'Please enter a valid postcode',
  stateValidationValidation = 'Enter your state',
}: InputAddressAutocompleteValidationConfig = {}) =>
  Yup.object({
    street: Yup.string()
      .required(streetRequiredValidation)
      .test('streetNameTooLong', streetTooLongValidation, validateStreetLength),
    apartment: Yup.string().max(20, maxCharacterValidation(20)),
    suburb: Yup.string()
      .required(suburbRequiredValidation)
      .max(30, maxCharacterValidation(30))
      .required(suburbRequiredValidation),
    postcode: Yup.string()
      .matches(postcodeRegex, postcodeFormatValidation)
      .max(20, maxCharacterValidation(20))
      .required(postcodeRequiredValidation),
    state: Yup.string()
      .oneOf(AUSTRALIAN_STATES)
      .max(20, maxCharacterValidation(20))
      .required(stateValidationValidation),
  });

export const OptionalInputAddressAutocompleteValidation = Yup.object({
  street: Yup.string(),
  apartment: Yup.string(),
  suburb: Yup.string(),
  postcode: Yup.string(),
  state: Yup.string(),
});

const defaultAddressValidation = getInputAddressAutocompleteValidation();

export type InputAddressAutocompleteAddress = Omit<
  ReturnType<typeof defaultAddressValidation.validateSync>,
  'state'
> & { state: any };

export const defaultValueInputAddressAutocomplete: Prettify<InputAddressAutocompleteAddress> = {
  street: '',
  apartment: '',
  suburb: '',
  postcode: '',
  state: '' as any,
};

type FormikInputAddressAutocompleteProps = {
  name: string;
  helperText?: ReactNode;
};

export const FormikInputAddressAutocomplete: FC<FormikInputAddressAutocompleteProps & FormikProps<any>> = ({
  name,
  helperText,
  setFieldValue,
  validateField,
  values,
}) => {
  const [_field, _meta] = useField(`${name}.street`);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);

  const { placePredictions, searchPlaces, clearPlacePredictions, fetchPlaceDetails, isLoading } =
    useAddressAutocomplete();

  const onPlaceSelect = async (prediction: PlacePrediction) => {
    setIsPlaceSelected(true);
    setIsDropdownOpen(false);
    resetAddress();

    const placeDetail = await fetchPlaceDetails(prediction.placeId);
    const { street, apartment, suburb, postcode, state } = extractAddressFromPlaceDetails(
      placeDetail,
      prediction.description,
    );
    fillAddress({ street, apartment, suburb, postcode, state });
  };

  const resetAddress = () => {
    setFieldValue(`${name}.apartment`, '');
    setFieldValue(`${name}.suburb`, '');
    setFieldValue(`${name}.postcode`, '');
    setFieldValue(`${name}.state`, undefined);
  };

  useEffect(() => {
    if (isPlaceSelected) revalidateField();
  }, [values[name]]);

  const revalidateField = () => {
    validateField(`${name}.suburb`);
    validateField(`${name}.postcode`);
    validateField(`${name}.state`);
    validateField(`${name}.street`);
  };

  const fillAddress = ({
    street,
    apartment,
    suburb,
    postcode,
    state,
  }: {
    apartment?: string;
    street?: string;
    suburb?: string;
    postcode?: string;
    state?: string;
  }) => {
    if (!isNil(street)) {
      setFieldValue(`${name}.street`, street, false);
    }
    if (!isNil(apartment)) {
      setFieldValue(`${name}.apartment`, apartment, false);
    }
    if (!isNil(suburb)) {
      setFieldValue(`${name}.suburb`, suburb, false);
    }
    if (!isNil(postcode)) {
      setFieldValue(`${name}.postcode`, postcode, false);
    }
    if (!isNil(state)) {
      setFieldValue(`${name}.state`, state, false);
    }
  };

  const onStreetInputChange = (_e: React.ChangeEvent<{}>, inputValue: string) => {
    if (inputValue) {
      searchPlaces(inputValue);
      setIsDropdownOpen(true);
    } else {
      clearPlacePredictions();
      fillAddress({ street: '', apartment: '', suburb: '', postcode: '', state: '' });
      setIsDropdownOpen(false);
    }
  };

  const onOpenChatWidget = () => {
    if (typeof window !== 'undefined' && typeof window.LiveChatWidget !== 'undefined' && !!window.LiveChatWidget)
      window.LiveChatWidget.call('maximize');

    pushToDataLayer({
      event: `checkout_click`,
      value: `license_contact_carma`,
    });
  };

  const showHelperText = !!values[name]?.state && values[name]?.state !== 'NSW';

  return (
    <div className={clsx({ 'mb-4': showHelperText })}>
      <AddressAutocomplete
        isOpen={isDropdownOpen}
        predictions={placePredictions}
        isLoading={isLoading}
        onPlaceSelect={onPlaceSelect}
      >
        <InputFormik
          id={`${name}.street`}
          autoComplete="street-address"
          type="text"
          name={`${name}.street`}
          data-testid="street-address"
          label="Street address"
          className={clsx('relative', inputFieldClassName)}
          onChangeCapture={(e) => {
            onStreetInputChange(e, e.currentTarget.value);
          }}
        />
      </AddressAutocomplete>
      <InputFormik
        id={`${name}.apartment`}
        name={`${name}.apartment`}
        label="Apartment, suite, etc (optional)"
        type="text"
        className={inputFieldClassName}
        data-testid="apartment"
      />
      <div className="grid grid-cols-2 gap-x-3.5 mdp:grid-cols-[2fr_1fr_1fr]">
        <InputFormik
          id={`${name}.suburb`}
          name={`${name}.suburb`}
          label="Suburb"
          type="text"
          autoComplete="address-level2"
          className={clsx('col-span-2 mdp:col-span-1', inputFieldClassName)}
          data-testid="suburb"
        />

        <InputFormik
          id={`${name}.postcode`}
          name={`${name}.postcode`}
          label="Postcode"
          type="text"
          autoComplete="postal-code"
          inputMode="numeric"
          maxLength={4}
          className={inputFieldClassName}
          data-testid="postcode"
        />

        <InputSelectFormik
          id={`${name}.state`}
          name={`${name}.state`}
          autoComplete="address-level1"
          label="State"
          allowEmpty
          options={AUSTRALIAN_STATES.map((state) => ({ value: state, label: state }))}
          className={inputFieldClassName}
          data-testid="state"
        />
      </div>
      {showHelperText && (
        <div>
          {helperText}
          <span>{`If you have any questions, `}</span>
          <button
            type="button"
            className="c-fs-caption m-0 cursor-pointer border-none bg-transparent p-0 font-extrabold text-tex-def-tie1 underline outline-none"
            onClick={onOpenChatWidget}
          >
            contact us.
          </button>
        </div>
      )}
    </div>
  );
};
